import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getArticleBySlug } from '../services/api';
import { ArrowLeft, Clock, User, Share2, ArrowRight, Moon, Sun, Minus, Plus } from 'lucide-react';
import { useNotification } from '../context/NotificationContext';

const ArticleDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Reading Preferences
  const [fontSize, setFontSize] = useState(18); // Default 18px
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getArticleBySlug(slug);
        if (data && data.article) {
          setArticle(data.article);
          setRelated(data.related || []);
        } else {
          setError('Artikel tidak ditemukan.');
        }
      } catch (err) {
        setError('Terjadi kesalahan saat memuat artikel.');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
    window.scrollTo(0, 0);
  }, [slug]);

  const getContent = (item, field) => {
    return item[`${field}_id`] || item[`${field}_en`] || '';
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('id-ID', options);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    addNotification('Link berhasil disalin!', 'success');
  };

  const adjustFontSize = (delta) => {
    setFontSize(prev => {
      const newSize = prev + delta;
      if (newSize < 14) return 14;
      if (newSize > 24) return 24;
      return newSize;
    });
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-zinc-900 text-zinc-300' : 'bg-white text-black'}`}>
        <div className="text-[10px] font-bold uppercase tracking-widest animate-pulse">Memuat Artikel...</div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center text-center p-6 ${darkMode ? 'bg-zinc-900 text-zinc-300' : 'bg-white text-black'}`}>
        <h2 className="text-2xl font-bold mb-4">Artikel Tidak Ditemukan</h2>
        <button 
          onClick={() => navigate('/articles')} 
          className="text-xs font-bold uppercase tracking-widest border-b border-current pb-1 hover:opacity-70 transition-all"
        >
          Kembali ke Beranda
        </button>
      </div>
    );
  }

  // Theme Classes
  const bgClass = darkMode ? 'bg-zinc-950' : 'bg-white';
  const textClass = darkMode ? 'text-zinc-300' : 'text-black';
  const headingClass = darkMode ? 'text-zinc-100' : 'text-zinc-900';
  const borderClass = darkMode ? 'border-zinc-800' : 'border-zinc-200';
  const proseClass = darkMode ? 'prose-invert prose-zinc' : 'prose-zinc';
  const buttonHoverClass = darkMode ? 'hover:bg-zinc-800' : 'hover:bg-zinc-100';

  return (
    <div className={`min-h-screen font-sans transition-colors duration-300 pt-20 ${bgClass} ${textClass}`}>
      
      {/* Static Top Bar */}
      <div className="max-w-7xl mx-auto px-6 pt-6 pb-2">
        <button 
          onClick={() => navigate('/articles')}
          className={`flex items-center gap-3 text-xs font-bold uppercase tracking-widest hover:text-blue-600 transition-colors py-2`}
          aria-label="Back"
        >
          <ArrowLeft size={16} /> Kembali ke Artikel
        </button>
      </div>

      <article className="max-w-screen-xl mx-auto">
        
        {/* Article Header */}
        <header className="max-w-4xl mx-auto px-6 pt-8 pb-12 text-center">
          <div className="flex justify-center items-center gap-2 mb-8">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-600">Deep Dive</span>
            <div className={`w-1 h-1 rounded-full ${darkMode ? 'bg-zinc-700' : 'bg-zinc-300'}`}></div>
            <span className={`text-[10px] font-bold uppercase tracking-[0.2em] ${darkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>Insight</span>
          </div>

          <h1 className={`text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.1] mb-8 ${headingClass}`}>
            {getContent(article, 'title')}
          </h1>

          <div className={`flex flex-col md:flex-row justify-center items-center gap-6 text-xs font-medium ${darkMode ? 'text-zinc-400 border-zinc-800' : 'text-zinc-500 border-zinc-100'} border-t border-b py-6 max-w-lg mx-auto`}>
             <div className="flex items-center gap-2">
               <User size={14} />
               <span className={`uppercase tracking-wider font-bold ${headingClass}`}>Redaksi iMoonusel</span>
             </div>
             <div className={`hidden md:block w-[1px] h-4 ${darkMode ? 'bg-zinc-700' : 'bg-zinc-300'}`}></div>
             <div className="flex items-center gap-2">
               <Clock size={14} />
               <span>{formatDate(article.created_at)}</span>
             </div>
             <div className={`hidden md:block w-[1px] h-4 ${darkMode ? 'bg-zinc-700' : 'bg-zinc-300'}`}></div>
             <div className={`flex items-center gap-2 cursor-pointer hover:opacity-70 transition-colors`} onClick={handleCopyLink}>
               <Share2 size={14} />
               <span className="uppercase tracking-wider">Bagikan</span>
             </div>
          </div>
        </header>

        {/* Featured Image */}
        <div className={`w-full aspect-[21/9] md:aspect-[2/1] overflow-hidden mb-12 ${darkMode ? 'bg-zinc-900' : 'bg-zinc-100'}`}>
          <img 
            src={article.featured_image || '/placeholder.jpg'} 
            alt={getContent(article, 'title')} 
            className="w-full h-full object-cover"
            onError={(e) => {e.target.src = 'https://via.placeholder.com/1600x900?text=No+Image'}}
          />
        </div>

        {/* Article Body & Controls Container */}
        <div className="max-w-2xl mx-auto px-6 pb-24">
          
          {/* Reading Controls Toolbar (New Location) */}
          <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 py-4 mb-12 border-y ${darkMode ? 'border-zinc-800' : 'border-black'}`}>
             
             {/* Text Size */}
             <div className="flex items-center gap-4">
               <span className={`text-[10px] font-bold uppercase tracking-widest ${darkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>Text Size</span>
               <div className={`flex items-center border ${borderClass}`}>
                  <button 
                    onClick={() => adjustFontSize(-1)} 
                    className={`p-3 ${buttonHoverClass} transition-colors border-r ${borderClass}`}
                    aria-label="Decrease Font"
                  >
                    <Minus size={14} />
                  </button>
                  <span className="w-12 text-center text-xs font-bold">{fontSize}px</span>
                  <button 
                    onClick={() => adjustFontSize(1)} 
                    className={`p-3 ${buttonHoverClass} transition-colors border-l ${borderClass}`}
                    aria-label="Increase Font"
                  >
                    <Plus size={14} />
                  </button>
               </div>
             </div>

             {/* Dark Mode */}
             <button 
               onClick={() => setDarkMode(!darkMode)}
               className={`flex items-center gap-3 px-6 py-3 border ${borderClass} ${buttonHoverClass} transition-all`}
             >
               {darkMode ? <Sun size={14} /> : <Moon size={14} />}
               <span className="text-[10px] font-bold uppercase tracking-widest">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
             </button>
          </div>

          {/* Content */}
          <div 
            className={`prose prose-lg ${proseClass} max-w-none 
              prose-headings:font-black prose-headings:tracking-tight ${darkMode ? 'prose-headings:text-zinc-100' : 'prose-headings:text-black'} 
              prose-p:font-serif ${darkMode ? 'prose-p:text-zinc-300' : 'prose-p:text-zinc-700'} prose-p:leading-relaxed 
              prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
              prose-img:rounded-none prose-img:transition-all
              first-letter:float-left first-letter:text-7xl first-letter:font-black ${darkMode ? 'first-letter:text-white' : 'first-letter:text-black'} first-letter:mr-3 first-letter:mt-[-10px]`}
            style={{ fontSize: `${fontSize}px`, lineHeight: '1.6' }}
            dangerouslySetInnerHTML={{ __html: getContent(article, 'content') }} 
          />

          {/* Share Footer */}
          <div className={`mt-16 pt-12 border-t ${darkMode ? 'border-zinc-800' : 'border-black'}`}>
            <h3 className="text-xs font-bold uppercase tracking-widest mb-6 text-center md:text-left">Sebarkan Wawasan Ini</h3>
            
            <div className="flex flex-wrap gap-3 justify-center md:justify-start">
               {/* WhatsApp */}
               <button 
                 onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(document.title + ' ' + window.location.href)}`, '_blank')} 
                 className={`w-12 h-12 flex items-center justify-center border hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-all group ${darkMode ? 'border-zinc-700 hover:border-[#25D366]' : 'border-zinc-200'}`}
                 aria-label="Share on WhatsApp"
               >
                 <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
               </button>

               {/* Twitter / X */}
               <button 
                 onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(document.title)}&url=${encodeURIComponent(window.location.href)}`, '_blank')} 
                 className={`w-12 h-12 flex items-center justify-center border hover:bg-black hover:text-white hover:border-black transition-all ${darkMode ? 'border-zinc-700 hover:bg-white hover:text-black hover:border-white' : 'border-zinc-200'}`}
                 aria-label="Share on X"
               >
                 <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
               </button>

               {/* Facebook */}
               <button 
                 onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')} 
                 className={`w-12 h-12 flex items-center justify-center border hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition-all ${darkMode ? 'border-zinc-700 hover:border-[#1877F2]' : 'border-zinc-200'}`}
                 aria-label="Share on Facebook"
               >
                 <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.791-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
               </button>

               {/* Reddit */}
               <button 
                 onClick={() => window.open(`https://www.reddit.com/submit?url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(document.title)}`, '_blank')} 
                 className={`w-12 h-12 flex items-center justify-center border hover:bg-[#FF4500] hover:text-white hover:border-[#FF4500] transition-all ${darkMode ? 'border-zinc-700 hover:border-[#FF4500]' : 'border-zinc-200'}`}
                 aria-label="Share on Reddit"
               >
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.249-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></svg>
               </button>

               {/* Gmail */}
               <button 
                 onClick={() => window.open(`mailto:?subject=${encodeURIComponent(document.title)}&body=${encodeURIComponent(window.location.href)}`, '_blank')} 
                 className={`w-12 h-12 flex items-center justify-center border rounded-full hover:bg-[#EA4335] hover:text-white hover:border-[#EA4335] transition-all ${darkMode ? 'border-zinc-700 hover:border-[#EA4335]' : 'border-zinc-200'}`}
                 aria-label="Share via Email"
               >
                 <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/></svg>
               </button>

               {/* Threads */}
               <button 
                 onClick={() => window.open(`https://www.threads.net/intent/post?text=${encodeURIComponent(document.title + ' ' + window.location.href)}`, '_blank')} 
                 className={`w-12 h-12 flex items-center justify-center border rounded-full hover:bg-black hover:text-white hover:border-black transition-all ${darkMode ? 'border-zinc-700 hover:bg-white hover:text-black hover:border-white' : 'border-zinc-200'}`}
                 aria-label="Share on Threads"
               >
                 <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12.688 18.528c-1.288-.04-2.508-.348-3.604-.84-2.736-1.232-4.14-3.8-3.48-6.904.304-1.424 1.096-2.6 2.304-3.444 1.256-.88 2.828-1.268 4.364-1.076 1.296.16 2.392.68 3.232 1.62.636.708.992 1.6.964 2.56-.032 1.144-.44 2.152-1.34 2.9-.668.552-1.488.8-2.352.748-.908-.052-1.616-.508-2.028-1.312-.132-.26-.204-.544-.228-.836-.068-.824.28-1.492.984-1.872.484-.26 1.028-.316 1.556-.168.104.028.14.072.124.184-.06.4-.356.584-.752.544-.304-.032-.508.064-.632.316-.104.208-.1.44-.02.66.14.384.456.616.852.632.616.024 1.06-.316 1.248-.896.184-1.336-.5-2.544-1.628-3.08-1.544-.732-3.4-.624-4.8.448-1.06.816-1.676 1.944-1.848 3.284-.368 2.864 1.48 5.684 4.196 6.456 1.956.556 3.868.22 5.588-1.016.144-.104.336-.072.436.064.104.14.08.336-.056.444-1.996 1.584-4.276 1.988-6.684 1.184zM12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 22c-5.523 0-10-4.477-10-10S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
               </button>

               {/* Instagram */}
               <button 
                 onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    window.open('https://instagram.com', '_blank');
                 }}
                 className={`w-12 h-12 flex items-center justify-center border rounded-full hover:bg-[#E4405F] hover:text-white hover:border-[#E4405F] transition-all ${darkMode ? 'border-zinc-700 hover:border-[#E4405F]' : 'border-zinc-200'}`}
                 aria-label="Instagram"
               >
                 <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
               </button>

               {/* Copy Link */}
               <button 
                 onClick={handleCopyLink} 
                 className={`w-12 h-12 flex items-center justify-center border rounded-full hover:bg-black hover:text-white hover:border-black transition-all ${darkMode ? 'border-zinc-700 hover:bg-white hover:text-black hover:border-white' : 'border-zinc-200'}`}
                 aria-label="Copy Link"
               >
                 <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>
               </button>
            </div>
          </div>
        </div>

        {/* Related Articles */}
        {related.length > 0 && (
          <section className={`py-24 border-t ${darkMode ? 'bg-zinc-950 border-zinc-800' : 'bg-zinc-50 border-zinc-200'}`}>
            <div className="max-w-7xl mx-auto px-6">
              <div className="flex items-center gap-4 mb-12">
                <div className="w-8 h-[2px] bg-blue-600"></div>
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-blue-600 italic">
                  Read Next
                </span>
              </div>
              
              <div className="grid md:grid-cols-3 gap-12">
                {related.map(item => (
                  <div key={item.id} className="cursor-pointer group" onClick={() => navigate(`/articles/${item.slug}`)}>
                    <div className={`aspect-[4/3] mb-6 overflow-hidden ${darkMode ? 'bg-zinc-900' : 'bg-zinc-200'}`}>
                      <img 
                        src={item.featured_image} 
                        alt={getContent(item, 'title')} 
                        className="w-full h-full object-cover transition-all duration-500"
                        onError={(e) => {e.target.style.display='none'}}
                      />
                    </div>
                    <h4 className={`text-xl font-bold leading-tight mb-3 transition-colors ${darkMode ? 'group-hover:text-blue-400 text-zinc-200' : 'group-hover:text-blue-800 text-black'}`}>
                      {getContent(item, 'title')}
                    </h4>
                    <span className={`text-[10px] font-bold uppercase tracking-widest transition-colors ${darkMode ? 'text-zinc-500 group-hover:text-blue-400' : 'text-zinc-400 group-hover:text-blue-600'}`}>
                      Baca Artikel <ArrowRight size={10} className="inline ml-1" />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
    </div>
  );
};

export default ArticleDetail;